// Platform-adaptive body for the "Get Beacon on your phone" modal (M3 Phase 2).
// One modal, branching on where it was opened: QR on desktop, Add-to-Home-Screen
// steps on iOS, a real Install button on Android (with an AHS fallback).
import { button, numberedSteps } from "../../design/components.js";
import { qrSvg } from "../../platform/qr.js";
import type { InstallTarget } from "../../platform/app-context.js";

type Tr = (k: string, p?: Record<string, string | number>) => string;

export interface InstallModalOpts {
  t: Tr;
  target: InstallTarget;
  /** The /get URL a desktop visitor scans to continue on their phone. */
  url: string;
  /** A beforeinstallprompt has been stashed (Android/Chromium). */
  installAvailable: boolean;
  onInstall: () => void;
  /** Optional self-hosted APK / F-Droid link (M1 Route B). */
  apkUrl?: string;
}

function el(tag: string, cls?: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

/** The gated You-tab entry that opens the install modal (browser context only). */
export function getBeaconCard(t: Tr, onGetApp: () => void): HTMLElement {
  const card = el("section", "you-card install-card");
  card.append(h_microlabel(t("you.get.title")));
  card.append(el("p", "bx-body", t("you.get.body")));
  const b = button({ label: t("you.get.cta"), variant: "secondary", onClick: onGetApp });
  b.setAttribute("data-action", "get-app");
  card.append(b);
  return card;
}

function h_microlabel(text: string): HTMLElement {
  return el("div", "bx-microlabel", text);
}

function homeScreenSteps(t: Tr): HTMLElement {
  return numberedSteps([t("install.ios.step.share"), t("install.ios.step.add"), t("install.ios.step.open")], -1);
}

export function installModalContent(o: InstallModalOpts): HTMLElement {
  const root = el("div", "install");

  if (o.target === "desktop") {
    root.append(el("p", "bx-body", o.t("install.desktop.intro")));
    const qr = el("div", "install__qr");
    qr.setAttribute("data-qr", "");
    qr.innerHTML = qrSvg(o.url);
    root.append(qr);
    const url = el("p", "bx-microlabel install__url", o.url);
    url.setAttribute("data-install-url", "");
    root.append(el("p", "bx-body", o.t("install.desktop.scan")), url);
    return root;
  }

  if (o.target === "android-chrome") {
    root.append(el("p", "bx-body", o.t("install.android.intro")));
    if (o.installAvailable) {
      const b = button({ label: o.t("install.android.install"), variant: "primary", onClick: o.onInstall });
      b.setAttribute("data-action", "install-app");
      root.append(b);
    } else {
      root.append(homeScreenSteps(o.t));
    }
    if (o.apkUrl) {
      const apk = el("a", "bx-microlabel install__apk", o.t("install.android.apk")) as HTMLAnchorElement;
      apk.href = o.apkUrl;
      apk.setAttribute("data-action", "install-apk");
      root.append(apk);
    }
    return root;
  }

  // iOS Safari and any other mobile browser: Add to Home Screen is the only path.
  root.append(
    el("p", "bx-body", o.t("install.ios.intro")),
    homeScreenSteps(o.t),
    el("p", "bx-microlabel install__note", o.t("install.ios.note")),
  );
  return root;
}
